import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";

import { OPTIMIZED_ASSIGNED_TAGS_ENDPOINT } from "constants/feature-flags";
import { TagTypes } from "constants/tags";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import {
  AssignedTag,
  AssignedTagFilter,
  AssignedTagInput,
  AssignedTagResponse
} from "./types/assigned-tag.type";

export interface BulkAssignTagsInput {
  tag_ids: number[];
  patients_filter: Record<string, any>;
  description?: string;
}

const buildListUrl = (type: string, filter: AssignedTagFilter): string => {
  const { useOptimizedEndpoint, ...rest } = filter;
  const prefix = useOptimizedEndpoint
    ? "assigned-tags-optimized/"
    : "assigned-tags/";
  return `${prefix}?type=${type}&${formatValidQueryParamUrlCommaSeparated(
    rest
  )}&limit=300`;
};

// Each tag type invalidates its own assigned-tags list plus the parent list that
// renders the chips, so the UI reflects add/update/remove after a refetch.
type AssignedTagInvalidationTag =
  | "AssignedSaleTags"
  | "Sales"
  | "AssignedPatientChartNoteTags"
  | "AssignedPatientProductTags"
  | "PatientProducts"
  | "AssignedAppointmentTags"
  | "Appointments"
  | "Events"
  | "VisitTrackersAppointments"
  | "AssignedPatientTags"
  | "Patients"
  | "StaffTasks"
  | "InventoryProductDetail";

const getAssignedTagInvalidations = (
  tag_type: TagTypes
): AssignedTagInvalidationTag[] => {
  if (tag_type === "Sale") {
    return ["AssignedSaleTags", "Sales"];
  } else if (tag_type === "ChartNote") {
    return ["AssignedPatientChartNoteTags"];
  } else if (tag_type === "PatientProduct") {
    return ["AssignedPatientProductTags", "PatientProducts"];
  } else if (tag_type === "Appointment") {
    return [
      "AssignedAppointmentTags",
      "Appointments",
      "Events",
      "VisitTrackersAppointments"
    ];
  } else if (tag_type === "Patient") {
    return ["AssignedPatientTags", "Patients"];
  } else if (tag_type === "Task") {
    return ["StaffTasks"];
  } else if (tag_type === "InventoryProduct") {
    // The clinic-stock list and the limit=300 product picker stay correct via
    // the optimistic onTagsChange/updateItem path, so don't refetch them here.
    // Only refresh the single-object detail query so it isn't stale on revisit.
    return ["InventoryProductDetail"];
  }
  return [];
};

export const assignedTagsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPatientAssignedTags: builder.query<
      AssignedTagResponse,
      AssignedTagFilter
    >({
      query: (filter) => buildListUrl("Patient", filter),
      providesTags: ["AssignedPatientTags"]
    }),
    getPatientProductAssignedTags: builder.query<
      AssignedTagResponse,
      AssignedTagFilter
    >({
      query: (filter) => buildListUrl("PatientProduct", filter),
      providesTags: ["AssignedPatientProductTags"]
    }),

    getAppointmentAssignedTags: builder.query<
      AssignedTagResponse,
      AssignedTagFilter
    >({
      query: (filter) => buildListUrl("Appointment", filter),
      providesTags: ["AssignedAppointmentTags"]
    }),
    getSaleAssignedTags: builder.query<AssignedTagResponse, AssignedTagFilter>({
      query: (filter) => buildListUrl("Sale", filter),
      providesTags: ["AssignedSaleTags"]
    }),

    getPatientChartNoteAssignedTags: builder.query<
      AssignedTagResponse,
      AssignedTagFilter
    >({
      query: (filter) => buildListUrl("ChartNote", filter),
      providesTags: ["AssignedPatientChartNoteTags"]
    }),

    addAssignedTag: builder.mutation<AssignedTag, AssignedTagInput>({
      query: ({ tag_type, ...body }) => ({
        url: `assigned-tags/`,
        method: "POST",
        body
      }),
      invalidatesTags: (result, _, { tag_type }) =>
        getAssignedTagInvalidations(tag_type)
    }),
    updateAssignedTag: builder.mutation<
      AssignedTag,
      Partial<AssignedTagInput> & { tag_type: TagTypes; id: number }
    >({
      query: ({ id, tag_type, ...body }) => ({
        url: `assigned-tags/${id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: (result, _, { tag_type }) =>
        getAssignedTagInvalidations(tag_type)
    }),
    removeAssignedTag: builder.mutation<
      AssignedTagResponse,
      { tag: number; tag_type: TagTypes }
    >({
      query: ({ tag }) => ({
        url: `assigned-tags/${tag}/`,
        method: "DELETE"
      }),
      invalidatesTags: (result, _, { tag_type }) =>
        getAssignedTagInvalidations(tag_type)
    }),
    bulkAssignTags: builder.mutation<
      { patients_count: number; bulk_action_id: number },
      BulkAssignTagsInput
    >({
      query: (body) => ({
        url: "assigned-tags/bulk/",
        method: "POST",
        body
      })
    })
  })
});

const {
  useGetPatientAssignedTagsQuery: useGetPatientAssignedTagsRawQuery,
  useLazyGetPatientAssignedTagsQuery: useLazyGetPatientAssignedTagsRawQuery,
  useGetPatientProductAssignedTagsQuery:
    useGetPatientProductAssignedTagsRawQuery,
  useGetAppointmentAssignedTagsQuery: useGetAppointmentAssignedTagsRawQuery,
  useGetSaleAssignedTagsQuery: useGetSaleAssignedTagsRawQuery,
  useGetPatientChartNoteAssignedTagsQuery:
    useGetPatientChartNoteAssignedTagsRawQuery,
  useLazyGetPatientChartNoteAssignedTagsQuery:
    useLazyGetPatientChartNoteAssignedTagsRawQuery,
  useAddAssignedTagMutation,
  useUpdateAssignedTagMutation,
  useRemoveAssignedTagMutation,
  useBulkAssignTagsMutation
} = assignedTagsApi;

const useOptimizedAssignedTagsFlag = (): boolean => {
  const flags = useFlags();
  return !!flags[OPTIMIZED_ASSIGNED_TAGS_ENDPOINT];
};

export const useGetPatientAssignedTagsQuery: typeof useGetPatientAssignedTagsRawQuery =
  (filter, options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    return useGetPatientAssignedTagsRawQuery(
      { ...filter, useOptimizedEndpoint },
      options
    );
  };

export const useLazyGetPatientAssignedTagsQuery: typeof useLazyGetPatientAssignedTagsRawQuery =
  (options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    const [trigger, result, lastArg] =
      useLazyGetPatientAssignedTagsRawQuery(options);
    const wrappedTrigger = useCallback(
      (filter, preferCacheValue) =>
        trigger({ ...filter, useOptimizedEndpoint }, preferCacheValue),
      [trigger, useOptimizedEndpoint]
    ) as typeof trigger;
    return [wrappedTrigger, result, lastArg];
  };

export const useGetPatientProductAssignedTagsQuery: typeof useGetPatientProductAssignedTagsRawQuery =
  (filter, options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    return useGetPatientProductAssignedTagsRawQuery(
      { ...filter, useOptimizedEndpoint },
      options
    );
  };

export const useGetAppointmentAssignedTagsQuery: typeof useGetAppointmentAssignedTagsRawQuery =
  (filter, options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    return useGetAppointmentAssignedTagsRawQuery(
      { ...filter, useOptimizedEndpoint },
      options
    );
  };

export const useGetSaleAssignedTagsQuery: typeof useGetSaleAssignedTagsRawQuery =
  (filter, options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    return useGetSaleAssignedTagsRawQuery(
      { ...filter, useOptimizedEndpoint },
      options
    );
  };

export const useGetPatientChartNoteAssignedTagsQuery: typeof useGetPatientChartNoteAssignedTagsRawQuery =
  (filter, options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    return useGetPatientChartNoteAssignedTagsRawQuery(
      { ...filter, useOptimizedEndpoint },
      options
    );
  };

export const useLazyGetPatientChartNoteAssignedTagsQuery: typeof useLazyGetPatientChartNoteAssignedTagsRawQuery =
  (options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    const [trigger, result, lastArg] =
      useLazyGetPatientChartNoteAssignedTagsRawQuery(options);
    const wrappedTrigger = useCallback(
      (filter, preferCacheValue) =>
        trigger({ ...filter, useOptimizedEndpoint }, preferCacheValue),
      [trigger, useOptimizedEndpoint]
    ) as typeof trigger;
    return [wrappedTrigger, result, lastArg];
  };

export {
  useAddAssignedTagMutation,
  useUpdateAssignedTagMutation,
  useRemoveAssignedTagMutation,
  useBulkAssignTagsMutation
};
